<template>
  <div class="dropdown" ref="dropdownEl">
    <div class="dropdown-header">
      <span>Saved searches</span>
      <button class="close-btn" @click="emit('close')" aria-label="Close"><X :size="14" /></button>
    </div>

    <div class="dropdown-body">
      <!-- Store current search -->
      <div class="store-row" v-if="!storingNew">
        <button class="store-btn" @click="storingNew = true">
          <Plus :size="14" /> Store current search
        </button>
      </div>
      <div class="store-row inline-form" v-else>
        <input v-model="newSearchName" placeholder="Name…" @keydown.enter="commitStore" @keydown.escape="storingNew = false" ref="nameInput" />
        <div class="alias-wrapper">
          <input
            v-model="newSearchAlias"
            placeholder="Quick toggle (opt.)"
            style="width: 6em;"
            title="Optional shortcut name for quick toggling in search bar"
          />
          <SmartTooltip>
            <template #trigger>
              <span class="help-icon" title="Click to learn more">?</span>
            </template>
            <template #content>
              Setting a quick toggle name lets you toggle this search on/off directly in the search bar without reopening this menu.
            </template>
          </SmartTooltip>
        </div>
        <button @click="commitStore" aria-label="Save"><Check :size="14" /></button>
        <button @click="storingNew = false" aria-label="Cancel"><X :size="14" /></button>
      </div>

      <!-- Current search (if on search page) -->
      <div v-if="props.currentSearchTags && props.currentSearchTags.length > 0">
        <!-- Store current search form -->
        <div class="store-row" v-if="!storingCurrent">
          <div class="current-search-row">
            <div class="search-info">
              <span class="search-name current-badge">Current Search</span>
              <span class="search-tags">{{ props.currentSearchTags.join(", ") }}</span>
            </div>
            <div class="search-actions">
              <button @click="emit('append', props.currentSearchTags)" title="Append to search" aria-label="Append"><Plus :size="14" /></button>
              <button @click="emit('replace', props.currentSearchTags)" title="Replace search" aria-label="Replace"><ArrowDownToLine :size="14" /></button>
              <button @click="storingCurrent = true" title="Save as favorite" aria-label="Save"><Check :size="14" /></button>
            </div>
          </div>
        </div>
        <div class="store-row inline-form" v-else>
          <input v-model="newSearchName" placeholder="Name…" @keydown.enter="commitStoreCurrent" @keydown.escape="storingCurrent = false" ref="nameInput" />
          <div class="alias-wrapper">
            <input
              v-model="newSearchAlias"
              placeholder="Quick toggle (opt.)"
              style="width: 6em;"
              title="Optional shortcut name for quick toggling in search bar"
            />
            <SmartTooltip>
              <template #trigger>
                <span class="help-icon" title="Click to learn more">?</span>
              </template>
              <template #content>
                Setting a quick toggle name lets you toggle this search on/off directly in the search bar without reopening this menu.
              </template>
            </SmartTooltip>
          </div>
          <button @click="commitStoreCurrent" aria-label="Save"><Check :size="14" /></button>
          <button @click="storingCurrent = false" aria-label="Cancel"><X :size="14" /></button>
        </div>
      </div>

      <!-- Saved search entries -->
      <div v-if="savedSearches.searches.length === 0 && (!props.currentSearchTags || props.currentSearchTags.length === 0)" class="empty-note">No saved searches yet.</div>

      <div v-for="s in savedSearches.searches" :key="s.id" class="search-row">
        <div class="search-info">
          <span class="search-name">
            <span v-if="s.alias" class="quick-toggle-badge" :title="`Quick toggle: ${s.alias}`">{{ s.alias }}</span>
            {{ s.name }}
          </span>
          <span class="search-tags">{{ s.tags.join(", ") }}</span>
        </div>
        <div class="search-actions">
          <button
            class="toggle-btn"
            :class="{ active: savedSearches.isActive(s.id) }"
            @click="savedSearches.toggleAlias(s.id)"
            title="Toggle (AND into search)"
            aria-label="Toggle alias"
          >
            <ToggleLeft :size="14" v-if="!savedSearches.isActive(s.id)" />
            <ToggleRight :size="14" v-else />
          </button>
          <button @click="emit('append', s.tags)" title="Append to search" aria-label="Append"><Plus :size="14" /></button>
          <button @click="emit('replace', s.tags)" title="Replace search" aria-label="Replace"><ArrowDownToLine :size="14" /></button>
          <button @click="savedSearches.remove(s.id)" title="Delete" aria-label="Delete" class="delete-btn"><Trash2 :size="14" /></button>
        </div>
      </div>

      <!-- Favorite tags section -->
      <div v-if="favoriteTags.tags.length > 0" class="section-divider">Favorite tags</div>
      <div v-if="favoriteTags.tags.length > 0" class="fav-tags">
        <button
          v-for="tag in favoriteTags.tags"
          :key="tag"
          class="fav-chip"
          :class="{ active: props.activeFavTags?.has(tag) }"
          @click="emit('toggleFavTag', tag)"
          :title="props.activeFavTags?.has(tag) ? `Remove from search: ${tag}` : `Add to search: ${tag}`"
        >
          {{ tag }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { nextTick, ref, watch } from "vue";
import { ArrowDownToLine, Check, Plus, ToggleLeft, ToggleRight, Trash2, X } from "lucide-vue-next";
import { useSavedSearchesStore } from "@/stores/savedSearches";
import { useFavoriteTagsStore } from "@/stores/favoriteTags";
import { useApiStore } from "@/stores/api";
import SmartTooltip from "@/components/SmartTooltip.vue";

const props = defineProps<{
  activeFavTags?: Set<string>;
  currentSearchTags?: string[];
}>();

const emit = defineEmits<{
  (e: "close"): void;
  (e: "append", tags: string[]): void;
  (e: "replace", tags: string[]): void;
  (e: "toggleFavTag", tag: string): void;
}>();

const savedSearches = useSavedSearchesStore();
const favoriteTags = useFavoriteTagsStore();
const apiConfig = useApiStore();

const storingNew = ref(false);
const storingCurrent = ref(false);
const newSearchName = ref("");
const newSearchAlias = ref("");
const nameInput = ref<HTMLInputElement | null>(null);

watch(() => storingNew.value, async (v) => {
  if (v) {
    await nextTick();
    nameInput.value?.focus();
  }
});

watch(() => storingCurrent.value, async (v) => {
  if (v) {
    await nextTick();
    nameInput.value?.focus();
  }
});

function commitStore() {
  if (!newSearchName.value.trim()) return;
  const currentQuery = apiConfig.searchQuery ?? "";
  const tags = currentQuery.split(",").map((t) => t.trim()).filter(Boolean);
  savedSearches.storeCurrentSearch(
    newSearchName.value.trim(),
    tags,
    newSearchAlias.value.trim() || undefined
  );
  newSearchName.value = "";
  newSearchAlias.value = "";
  storingNew.value = false;
}

function commitStoreCurrent() {
  if (!newSearchName.value.trim() || !props.currentSearchTags) return;
  savedSearches.storeCurrentSearch(
    newSearchName.value.trim(),
    props.currentSearchTags,
    newSearchAlias.value.trim() || undefined
  );
  newSearchName.value = "";
  newSearchAlias.value = "";
  storingCurrent.value = false;
}
</script>

<style scoped lang="scss">
/* Position is injected via :style from SearchBar.vue (Teleport + getBoundingClientRect) */
.dropdown {
  z-index: 2000;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  width: min(360px, 95vw);
  box-shadow: 0 4px 16px rgba(0,0,0,0.3);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.dropdown-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: var(--bg-elevated);
  border-bottom: 1px solid var(--border);
  font-size: 0.85rem;
  font-weight: 600;

  .close-btn {
    background: none;
    border: none;
    cursor: pointer;
    color: var(--text-primary);
    &:hover { color: var(--accent); }
  }
}

.dropdown-body {
  overflow-y: auto;
  max-height: 320px;
  padding: 8px 0;
}

.store-row {
  padding: 4px 12px;
}

.store-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: 1px dashed var(--border);
  border-radius: var(--radius-sm);
  color: var(--text-primary);
  cursor: pointer;
  padding: 5px 10px;
  font-size: 0.8rem;
  width: 100%;
  &:hover { border-color: var(--accent); color: var(--accent); }
}

.inline-form {
  display: flex;
  gap: 4px;
  align-items: center;

  input {
    flex: 1;
    background: var(--bg-elevated);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    color: var(--text-primary);
    padding: 4px 6px;
    font-size: 0.8rem;
    min-width: 0;

    &[placeholder*="Quick toggle"] {
      flex: 0.8;
    }
  }

  button {
    background: none;
    border: none;
    cursor: pointer;
    color: var(--text-primary);
    padding: 2px;
    &:hover { color: var(--accent); }
  }
}

.alias-wrapper {
  position: relative;
  flex: 0.8;
  display: flex;
  gap: 2px;
  align-items: center;

  input {
    flex: 1;
    min-width: 0;
  }
}

.help-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--accent);
  color: #fff;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: help;
  opacity: 0.8;
  transition: opacity var(--transition-fast);

  &:hover {
    opacity: 1;
  }
}

.empty-note {
  padding: 8px 12px;
  font-size: 0.8rem;
  opacity: 0.6;
  text-align: center;
}

.search-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 6px 12px;
  border-bottom: 1px solid var(--border);

  &:last-of-type { border-bottom: none; }
  &:hover { background: var(--btn-hover-bg); }
}

.search-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.search-name {
  font-size: 0.85rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
}

.alias-badge {
  background: var(--accent);
  color: #fff;
  border-radius: 3px;
  padding: 0 4px;
  font-size: 0.75em;
}

.search-tags {
  font-size: 0.75rem;
  opacity: 0.7;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.search-actions {
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;

  button {
    background: none;
    border: none;
    cursor: pointer;
    color: var(--text-primary);
    padding: 3px;
    opacity: 0.6;
    border-radius: var(--radius-sm);

    &:hover { opacity: 1; color: var(--accent); }
    &.active { opacity: 1; color: var(--accent); }
    &.delete-btn:hover { color: #e55; }
  }
}

.current-search-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  padding: 6px 0;
  border-bottom: 2px solid var(--accent-border);

  &:hover { background: var(--btn-hover-bg); }

  .search-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .search-name {
    font-size: 0.85rem;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .search-tags {
    font-size: 0.75rem;
    opacity: 0.7;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .search-actions {
    display: flex;
    align-items: center;
    gap: 2px;
    flex-shrink: 0;

    button {
      background: none;
      border: none;
      cursor: pointer;
      color: var(--text-primary);
      padding: 3px;
      opacity: 0.6;
      border-radius: var(--radius-sm);

      &:hover { opacity: 1; color: var(--accent); }
      &.active { opacity: 1; color: var(--accent); }
    }
  }
}

.quick-toggle-badge,
.current-badge {
  background: var(--accent);
  color: #fff;
  border-radius: 3px;
  padding: 0 4px;
  font-size: 0.75em;
  font-weight: 600;
}

.section-divider {
  padding: 6px 12px 2px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--text-accent);
  opacity: 0.8;
}

.fav-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 4px 12px 8px;
}

.fav-chip {
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 3px 8px;
  font-size: 0.75rem;
  cursor: pointer;
  color: var(--text-primary);
  &:hover { border-color: var(--accent); color: var(--accent); }
  &.active { background: var(--accent); color: #fff; border-color: var(--accent); }
}
</style>
